// Capacitor import 제거됨 - CapacitorBadge는 App.tsx에서 관리
import { t, Trans } from '@lingui/macro';
import { Bell, X, MessagesSquare } from "lucide-react";
import { useEffect, useState, createContext, useContext, ReactNode } from "react";
import { useHistory } from "../../hooks/useRouterCompat";
import { useAppUIContext } from "../../context/AppUIContext";
import { Notification } from "../../models/Notification";
import { Fire } from "../../services/FireClient";
import { My } from "../../services/My";
import { MyNotifs } from "../../services/MyNotifs";
import { NotificationUtil } from "../../services/Utils/NotificationUtil";
import { SoundUtil } from "../../services/Utils/SoundUtil";
import { Util } from "../../services/Utils/Util";
import { UtilApp } from "../../services/Utils/UtilApp";
// CapacitorBadge는 App.tsx에서 반응형으로 관리됨
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { cn } from "../../lib/utils";

const NotificationModal: React.FC = () => {
  const history = useHistory();
  const { setMessageToShow, setIsMobileMenuOpen } = useAppUIContext();
  const { setNotification } = useMessageContext();
  const [open, setOpen] = useState(false);
  const [showClearAlert, setShowClearAlert] = useState(false);

  useEffect(() => {
    // play sound on new notification
    let unsubNotifs = MyNotifs.subscribeToNotificationUpdates(async (notifs: Notification[]) => {
      if (notifs.length > 0 && UtilApp.getPlatform() == 'web' && MyNotifs.NrOfNewLastChange > 0 && !document.hasFocus()) {
        if (notifs[0].type != 'CallStart') {
          if (!My.user.roomSettings?.find(n => n.roomId == notifs[0].roomId && n.disablePushNotifications)) {
            if (notifs[0].type != 'Emoticon') {
              SoundUtil.playNewMessage();
            } else {
              SoundUtil.playNewReaction();
            }
          }
        }
      }
    });

    return () => { unsubNotifs(); };
  }, []);

  function dismissed() {
    Fire.clearNotifications(MyNotifs.Notifications.filter(n => n.isNew && n.type == 'RoomAdded'));
  }

  // ✅ OS 뱃지는 App.tsx의 subscribeToNewNotifications 콜백에서 반응형으로 관리됨

  function clearAllNotifs() {
    Fire.clearNotifications(MyNotifs.Notifications.filter(n => n.isNew));
    setShowClearAlert(false);
  }

  function itemClick(notif: Notification) {
    console.log("[LOG 1] Notification clicked:", {
      notifId: notif.id,
      roomId: notif.roomId,
      messageId: notif.messageId,
      parentMessageId: notif.parentMessageId,
      replyToMessageId: notif.replyToMessageId,
      type: notif.type,
      fromUser: notif.fromUser?.name
    });
    if (notif.roomId) {
      // 모달과 메뉴 닫기 + 바로 네비게이션
      setOpen(false);
      setIsMobileMenuOpen(false);
      setNotification(notif);
      history.push(`/room/${notif.roomId}`, { fromNotification: true, notification: notif, timestamp: Date.now() });
    }
  }

  async function showMessage(id: string, e: React.MouseEvent) {
    e.stopPropagation();
    Fire.msg.getMessage(id).then(message => {
      if (message) {
        setMessageToShow(message); // MessageModal로 메시지 표시
      } else {
        alert(t`Message could not be found. It may have been deleted.`);
      }
    }).catch(e => {
      console.error(e);
      alert(t`Message could not be found. It may have been deleted.`);
    });
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) dismissed();
      }}>
        <DialogTrigger asChild>
          <Button variant="ghost" className="relative [&_svg]:h-6 [&_svg]:w-6" data-cy="notif">
            <Bell />
            {MyNotifs.NrOfNew > 0 && (
              <Badge className="absolute -top-2 left-1/2 -translate-x-1/2 h-5 min-w-[1.25rem] px-3 py-3 flex items-center justify-center text-[10px] bg-orange-500/80 text-white rounded-full">
                {MyNotifs.NrOfNew}
              </Badge>
            )}
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-2xl max-h-[80vh] p-0 flex flex-col">
          <DialogHeader className="px-6 py-4 border-b flex-row items-center space-y-0">
            <DialogTitle className="text-lg font-semibold">
              <Trans>Notifications</Trans>
            </DialogTitle>
            <AlertDialog open={showClearAlert} onOpenChange={setShowClearAlert}>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="sm" className="text-primary ml-8">
                  <Trans>Mark all as seen</Trans>
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    <Trans>Are you sure you want to mark all notifications as seen?</Trans>
                  </AlertDialogTitle>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
                  <AlertDialogAction onClick={clearAllNotifs}>
                    <Trans>Yes</Trans>
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto">
            {MyNotifs.Notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                <img
                  src="/assets/img/img-cover-sleep-160.svg"
                  alt="No notifications"
                  className="w-40 h-40 mb-4 opacity-50"
                />
                <p className="text-gray-500">
                  <Trans>No notifications</Trans>
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {MyNotifs.Notifications.map(notif => {
                  const isHidden = notif.roomId && My.RoomSettings.get(notif.roomId)?.hideRoomInMyList;

                  return (
                    <div
                      key={notif.id}
                      className={cn(
                        "flex items-start gap-3 p-4 cursor-pointer hover:bg-gray-50 transition-colors border-l-4",
                        notif.isNew
                          ? (isHidden ? "border-l-gray-400 bg-gray-50/50" : "border-l-orange-400 bg-orange-50/30")
                          : "border-l-transparent"
                      )}
                      onClick={() => itemClick(notif)}
                    >
                      <Avatar className="w-10 h-10 shrink-0">
                        <AvatarImage src={notif.fromUser?.photoUrl || undefined} />
                        <AvatarFallback className="bg-gray-200 text-gray-600 text-sm">
                          {notif.fromUser?.name?.[0] || '?'}
                        </AvatarFallback>
                      </Avatar>

                      <div className="flex-1 min-w-0">
                        <div className={cn(" text-xs mb-1", notif.isNew && "font-bold")}>
                          <span className="font-semibold text-orange-400">
                            {Util.isOneToOneRoom(notif.roomName)
                              ? notif.fromUser?.name
                              : `#${notif.roomName}`}
                          </span>
                          <span className="text-gray-600"> @{notif.fromUser?.name}</span>
                        </div>
                        <div className="text-sm text-gray-600 line-clamp-2">
                          {NotificationUtil.getSummaryTranslated(notif)}
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {UtilApp.dateStrToTime(notif.created)},
                          {UtilApp.dateStrToDay(notif.created, My.user.lang)}
                        </span>
                        {notif.messageId && (
                          <Button
                            className="w-8 h-8 mt-1"
                            variant="icon-box"
                            size="icon"
                            onClick={(e) => showMessage(notif.messageId!, e)}
                          >
                            <MessagesSquare />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

interface MessageContextType {
  notification: Notification | null;
  setNotification: (id: Notification | null) => void;
}

const MessageContext = createContext<MessageContextType | undefined>(undefined);

export const MessageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [notification, setNotification] = useState<Notification | null>(null);

  return (
    <MessageContext.Provider value={{ notification, setNotification }}>
      {children}
    </MessageContext.Provider>
  );
};

export const useMessageContext = (): MessageContextType => {
  const context = useContext(MessageContext);
  if (!context) {
    throw new Error("useMessageContext must be used within a MessageProvider");
  }
  return context;
};

export default NotificationModal;